import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { ALL_MEAL_TYPES, isoDate } from "../lib/utils";

export default function MealPlanNewPage() {
  const navigate = useNavigate();
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const [name, setName] = useState(`Týden ${monday.getDate()}.${monday.getMonth() + 1}.`);
  const [dateFrom, setDateFrom] = useState(isoDate(monday));
  const [dateTo, setDateTo] = useState(isoDate(sunday));
  const [mealTypes, setMealTypes] = useState<string[]>(["snidane", "obed", "vecere"]);
  const [defaultServings, setDefaultServings] = useState<number | "">(2);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/api/meal-plans", {
        name: name.trim(),
        dateFrom,
        dateTo,
        mealTypes,
        defaultServings: Number(defaultServings) || 2,
      }),
    onSuccess: (res) => navigate(`/jidelnicky/${res.id}`),
  });

  return (
    <div>
      <Link to="/jidelnicky" className="text-sm text-slate-500 hover:underline">
        ← Zpět na jídelníčky
      </Link>
      <PageHeader icon="📅" title="Nový jídelníček" />

      <div className="card card-body max-w-2xl space-y-4">
        <div>
          <label className="label">Název</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Od</label>
            <input className="input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">Do</label>
            <input className="input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">Typy jídel v jídelníčku</label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {ALL_MEAL_TYPES.map((mt) => (
              <label key={mt.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={mealTypes.includes(mt.id)}
                  onChange={(e) =>
                    setMealTypes((arr) =>
                      e.target.checked ? [...arr, mt.id] : arr.filter((x) => x !== mt.id)
                    )
                  }
                />
                {mt.label}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="label flex items-center gap-1">
            <span aria-hidden>🍽️</span>
            <span>Výchozí počet porcí</span>
          </label>
          <input
            className="input max-w-[160px]"
            type="number"
            min={1}
            step="0.5"
            value={defaultServings}
            onChange={(e) => setDefaultServings(e.target.value === "" ? "" : Number(e.target.value))}
          />
          <p className="text-xs text-slate-500 mt-1">
            Při přidávání jednotlivých jídel se předvyplní tato hodnota. Můžete ji u každého jídla individuálně přepsat.
          </p>
        </div>
        <div>
          <button
            className="btn-primary flex items-center gap-1"
            disabled={!name.trim() || !dateFrom || !dateTo || mealTypes.length === 0 || create.isPending}
            onClick={() => create.mutate()}
          >
            <span aria-hidden>➕</span>
            <span>Vytvořit</span>
          </button>
          {create.isError && (
            <p className="text-sm text-red-600 mt-2">{(create.error as Error).message}</p>
          )}
        </div>
      </div>
    </div>
  );
}
